import type { ButtonHTMLAttributes, InputHTMLAttributes, ReactNode, SelectHTMLAttributes } from 'react';
import { useId, useState } from 'react';
import type { Severity } from '@meshalert/contract';
import { SEVERITY_LABEL } from '../lib/format.ts';

/**
 * The console's building blocks. Owned components rather than a library in its
 * default state: an alerting UI lives or dies on how severity reads at a
 * glance, and that is not something a generic theme gets right.
 */

export function cx(...parts: (string | false | null | undefined)[]): string {
  return parts.filter(Boolean).join(' ');
}

/* ---------------------------------------------------------------- buttons */

type ButtonProps = ButtonHTMLAttributes<HTMLButtonElement> & {
  variant?: 'primary' | 'secondary' | 'ghost' | 'danger';
  size?: 'sm' | 'md';
  icon?: ReactNode;
};

const BUTTON_VARIANTS = {
  primary: 'bg-accent text-accent-fg hover:bg-accent-hover',
  secondary: 'bg-raised text-ink border border-line-strong hover:bg-hover',
  ghost: 'text-ink-2 hover:bg-hover hover:text-ink',
  danger: 'bg-raised text-critical border border-critical/40 hover:bg-critical-soft',
} as const;

export function Button({
  variant = 'secondary',
  size = 'md',
  icon,
  className,
  children,
  ...rest
}: ButtonProps) {
  return (
    <button
      {...rest}
      className={cx(
        'inline-flex items-center justify-center gap-1.5 rounded-md font-medium whitespace-nowrap',
        'transition-colors duration-150 active:translate-y-px',
        'disabled:pointer-events-none disabled:opacity-45',
        size === 'sm' ? 'h-7 px-2.5 text-[13px]' : 'h-9 px-3.5 text-sm',
        BUTTON_VARIANTS[variant],
        className,
      )}
    >
      {icon}
      {children}
    </button>
  );
}

/* ------------------------------------------------------------------ forms */

type FieldProps = {
  label: string;
  hint?: string;
  error?: string;
  children: (id: string) => ReactNode;
};

/** Label above, hint under the label, error below the control. Always. */
export function Field({ label, hint, error, children }: FieldProps) {
  const id = useId();
  return (
    <div className="flex flex-col gap-1.5">
      <label htmlFor={id} className="text-[13px] font-medium text-ink">
        {label}
      </label>
      {hint ? <p className="-mt-1 text-[12px] text-ink-3">{hint}</p> : null}
      {children(id)}
      {error ? (
        <p role="alert" className="text-[12px] text-critical">
          {error}
        </p>
      ) : null}
    </div>
  );
}

const CONTROL_CLASS =
  'h-9 w-full rounded-md border border-line-strong bg-raised px-2.5 text-sm text-ink ' +
  'placeholder:text-ink-3 transition-colors focus:border-accent focus:outline-none ' +
  'focus:ring-2 focus:ring-accent/25 disabled:opacity-50';

export function Input({ className, ...rest }: InputHTMLAttributes<HTMLInputElement>) {
  return <input {...rest} className={cx(CONTROL_CLASS, className)} />;
}

export function Select({ className, children, ...rest }: SelectHTMLAttributes<HTMLSelectElement>) {
  return (
    <select {...rest} className={cx(CONTROL_CLASS, 'pr-8', className)}>
      {children}
    </select>
  );
}

export function Textarea({
  className,
  ...rest
}: InputHTMLAttributes<HTMLTextAreaElement> & { rows?: number }) {
  return (
    <textarea
      {...rest}
      className={cx(CONTROL_CLASS, 'h-auto py-2 leading-relaxed resize-y', className)}
    />
  );
}

export function Checkbox({
  label,
  description,
  ...rest
}: InputHTMLAttributes<HTMLInputElement> & { label: string; description?: string }) {
  const id = useId();
  return (
    <div className="flex items-start gap-2.5">
      <input
        {...rest}
        id={id}
        type="checkbox"
        className="mt-0.5 size-4 shrink-0 rounded-sm accent-[var(--accent)]"
      />
      <label htmlFor={id} className="cursor-pointer select-none">
        <span className="block text-[13px] font-medium text-ink">{label}</span>
        {description ? <span className="block text-[12px] text-ink-3">{description}</span> : null}
      </label>
    </div>
  );
}

/* ------------------------------------------------------------------- tabs */

/**
 * Segmentierte Umschaltung. Bewusst kein Router-Zustand: die Abschnitte einer
 * Seite sind kein eigener Ort, zu dem jemand verlinken müsste.
 */
export function Tabs<T extends string>({
  value,
  onChange,
  items,
}: {
  value: T;
  onChange: (next: T) => void;
  items: { value: T; label: string; count?: number }[];
}) {
  return (
    <div role="tablist" className="inline-flex rounded-md border border-line bg-sunken p-0.5">
      {items.map((item) => {
        const active = item.value === value;
        return (
          <button
            key={item.value}
            role="tab"
            type="button"
            aria-selected={active}
            onClick={() => onChange(item.value)}
            className={cx(
              'inline-flex h-7 items-center gap-1.5 rounded-[5px] px-3 text-[13px] font-medium transition-colors',
              active ? 'bg-raised text-ink shadow-[var(--shadow-card)]' : 'text-ink-3 hover:text-ink-2',
            )}
          >
            {item.label}
            {item.count !== undefined ? (
              <span className={cx('tnum text-[11px]', active ? 'text-ink-3' : 'text-ink-3/70')}>
                {item.count}
              </span>
            ) : null}
          </button>
        );
      })}
    </div>
  );
}

/* ---------------------------------------------------------------- kopieren */

/** Kopieren mit Rückmeldung — ohne die bestätigt niemand, dass es geklappt hat. */
export function CopyButton({ value, size = 'sm' }: { value: string; size?: 'sm' | 'md' }) {
  const [copied, setCopied] = useState(false);
  return (
    <Button
      size={size}
      onClick={async () => {
        await navigator.clipboard.writeText(value);
        setCopied(true);
        setTimeout(() => setCopied(false), 1600);
      }}
    >
      {copied ? 'Kopiert' : 'Kopieren'}
    </Button>
  );
}

export function CodeBlock({ children, copy }: { children: string; copy?: boolean }) {
  return (
    <div className="relative">
      <pre className="overflow-x-auto rounded-md border border-line bg-sunken p-3 font-mono text-[11.5px] leading-relaxed text-ink-2">
        {children}
      </pre>
      {copy ? (
        <div className="absolute top-2 right-2">
          <CopyButton value={children} />
        </div>
      ) : null}
    </div>
  );
}

/* ---------------------------------------------------------------- surface */

export function Panel({
  title,
  action,
  children,
  className,
  bodyClassName,
}: {
  title?: string;
  action?: ReactNode;
  children: ReactNode;
  className?: string;
  bodyClassName?: string;
}) {
  return (
    <section
      className={cx('rounded-lg border border-line bg-raised shadow-[var(--shadow-card)]', className)}
    >
      {title || action ? (
        <header className="flex h-12 items-center justify-between gap-3 border-b border-line px-4">
          <h2 className="text-[13px] font-semibold tracking-[0.01em] text-ink">{title}</h2>
          {action}
        </header>
      ) : null}
      <div className={bodyClassName ?? 'p-4'}>{children}</div>
    </section>
  );
}

/* --------------------------------------------------------------- severity */

const SEVERITY_TEXT: Record<Severity, string> = {
  critical: 'text-critical',
  error: 'text-error',
  warning: 'text-warning',
  info: 'text-info',
};

const SEVERITY_CHIP: Record<Severity, string> = {
  critical: 'bg-critical-soft text-critical',
  error: 'bg-error-soft text-error',
  warning: 'bg-warning-soft text-warning',
  info: 'bg-info-soft text-info',
};

const SEVERITY_RULE: Record<Severity, string> = {
  critical: 'bg-critical',
  error: 'bg-error',
  warning: 'bg-warning',
  info: 'bg-info',
};

export function severityTextClass(severity: Severity): string {
  return SEVERITY_TEXT[severity];
}

export function severityRuleClass(severity: Severity): string {
  return SEVERITY_RULE[severity];
}

export function SeverityChip({ severity }: { severity: Severity }) {
  return (
    <span
      className={cx(
        'inline-flex h-5 items-center rounded-sm px-1.5 text-[11px] font-semibold',
        SEVERITY_CHIP[severity],
      )}
    >
      {SEVERITY_LABEL[severity]}
    </span>
  );
}

export function StatusChip({ status }: { status: 'firing' | 'acknowledged' | 'resolved' }) {
  const style =
    status === 'firing'
      ? 'border-critical/45 text-critical'
      : status === 'acknowledged'
        ? 'border-warning/45 text-warning'
        : 'border-line-strong text-ink-3';

  return (
    <span
      className={cx(
        'inline-flex h-5 items-center gap-1.5 rounded-sm border px-1.5 text-[11px] font-medium',
        style,
      )}
    >
      {status === 'firing' ? (
        <span aria-hidden className="live-pulse size-1.5 rounded-full bg-critical" />
      ) : null}
      {status === 'firing' ? 'Offen' : status === 'acknowledged' ? 'Quittiert' : 'Behoben'}
    </span>
  );
}

export function Tag({ children }: { children: ReactNode }) {
  return (
    <span className="inline-flex h-5 items-center rounded-sm bg-sunken px-1.5 font-mono text-[11px] text-ink-2">
      {children}
    </span>
  );
}

/* -------------------------------------------------------------- kennzahlen */

/** Eine Zahl mit Beschriftung. Mono, damit sie beim Nachladen nicht springt. */
export function StatTile({
  label,
  value,
  note,
  loading,
  tone = 'neutral',
}: {
  label: string;
  value: number | string | undefined;
  note?: string;
  loading: boolean;
  tone?: 'neutral' | 'critical' | 'warning';
}) {
  return (
    <div className="rounded-lg border border-line bg-raised p-4 shadow-[var(--shadow-card)]">
      <p className="text-[12px] text-ink-3">{label}</p>
      {loading ? (
        <Skeleton className="mt-2 h-8 w-16" />
      ) : (
        <p
          className={cx(
            'tnum mt-1 text-[30px] leading-none font-medium tracking-tight',
            tone === 'critical' ? 'text-critical' : tone === 'warning' ? 'text-warning' : 'text-ink',
          )}
        >
          {value ?? '--'}
        </p>
      )}
      <p className="mt-2 h-4 text-[12px] text-ink-3">{loading ? '' : (note ?? '')}</p>
    </div>
  );
}

/* ------------------------------------------------------------ page states */

export function Skeleton({ className }: { className?: string }) {
  return <div className={cx('skeleton rounded-md', className)} />;
}

/** Matches the shape of the list it replaces, so nothing jumps when data lands. */
export function ListSkeleton({ rows = 5 }: { rows?: number }) {
  return (
    <div className="divide-y divide-[var(--border-subtle)]">
      {Array.from({ length: rows }, (_, i) => (
        <div key={i} className="flex items-center gap-4 px-4 py-3.5">
          <Skeleton className="h-4 w-16" />
          <Skeleton className="h-4 flex-1" />
          <Skeleton className="h-4 w-24" />
        </div>
      ))}
    </div>
  );
}

export function EmptyState({
  icon,
  title,
  description,
  action,
}: {
  icon?: ReactNode;
  title: string;
  description: string;
  action?: ReactNode;
}) {
  return (
    <div className="flex flex-col items-center gap-3 px-6 py-14 text-center">
      {icon ? <div className="text-ink-3">{icon}</div> : null}
      <div className="space-y-1">
        <h3 className="text-sm font-semibold text-ink">{title}</h3>
        <p className="mx-auto max-w-[46ch] text-[13px] leading-relaxed text-ink-2">{description}</p>
      </div>
      {action}
    </div>
  );
}

export function ErrorState({ message, onRetry }: { message: string; onRetry?: () => void }) {
  return (
    <div className="flex flex-col items-center gap-3 px-6 py-12 text-center">
      <p className="max-w-[52ch] text-[13px] text-ink">{message}</p>
      {onRetry ? (
        <Button size="sm" onClick={onRetry}>
          Erneut versuchen
        </Button>
      ) : null}
    </div>
  );
}

/* ------------------------------------------------------------------ table */

export function Th({ children, className }: { children?: ReactNode; className?: string }) {
  return (
    <th
      scope="col"
      className={cx(
        'h-9 px-4 text-left text-[11px] font-semibold tracking-wide text-ink-3',
        className,
      )}
    >
      {children}
    </th>
  );
}

export function Td({ children, className }: { children?: ReactNode; className?: string }) {
  return <td className={cx('px-4 py-3 align-middle text-[13px] text-ink-2', className)}>{children}</td>;
}
